// Datadog RUM のブラウザ計装。ブラウザで起きたエラーを Datadog (AP1) へ送る。
import { datadogRum } from "@datadog/browser-rum";

// applicationId と clientToken はブラウザへ出る公開値（秘密情報ではない）。Vercel の
// 環境変数に両方が入っている環境でだけ初期化するので、ローカル開発では送信されない。
const applicationId = process.env.NEXT_PUBLIC_DATADOG_RUM_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DATADOG_RUM_CLIENT_TOKEN;

// Clerk は招待やハンドシェイクのリンクで __clerk_ticket などをクエリに載せる。RUM は
// ビューの URL をクエリ込みで記録するため、送信前にクエリとフラグメントを落とす。
const stripQuery = (url: string) => url.split(/[?#]/)[0] ?? url;

// beforeSend が効くのは RUM イベントだけで、Session Replay には届かない。レコーダーは
// view が作られるたびに window.location.href を Meta レコードへ入れるため、トークンが
// 載りうる URL では録画そのものを止める。
const REPLAY_BLOCKED_PATH = /\/(sign-in|sign-up)(\/|$)/;
// history のラッパーから呼ぶので、URL を解析できなくても例外を出さない。判断できない
// ものは録画しない側へ倒す。
const replayAllowed = (url: unknown, base?: string) => {
  try {
    const parsed = new URL(String(url), base);
    return !REPLAY_BLOCKED_PATH.test(parsed.pathname) && !parsed.search.includes("__clerk");
  } catch {
    return false;
  }
};
const syncReplayRecording = () => {
  if (replayAllowed(window.location.href)) {
    datadogRum.startSessionReplayRecording();
  } else {
    datadogRum.stopSessionReplayRecording();
  }
};

if (applicationId && clientToken) {
  // popstate は RUM も購読して view を作る。同じ target のリスナーは登録順に走るので、
  // init より先に登録し、戻る操作で URL が記録される前に停止できるようにする。
  window.addEventListener("popstate", syncReplayRecording);

  datadogRum.init({
    applicationId,
    clientToken,
    site: "ap1.datadoghq.com",
    service: "velpha-docs",
    // Vercel の System Environment Variables 公開設定に依存する。取得できなかったときに
    // dev のノイズへ紛れないよう、既定値は unknown にしておく。
    env: process.env.NEXT_PUBLIC_VERCEL_ENV ?? "unknown",
    // ソースマップのアップロード時に渡す release-version と一致させる（package.json の
    // sourcemaps スクリプト）。ここがずれるとスタックトレースが復元されない。
    version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    sessionSampleRate: 100,
    // 利用者が少ないうちは全セッションを録画する。増えてきたら下げる。
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
    // 記録は全面マスクで行う。テキストは x に、画像はプレースホルダに置換される。個別に
    // 開けたい要素があれば data-dd-privacy="allow" を付ける。
    defaultPrivacyLevel: "mask",
    // リプレイをアクション単位で辿れるよう操作イベントは収集する。アクション名は
    // enablePrivacyForActionName の既定（有効）でマスクされる。
    trackUserInteractions: true,
    // リソースイベントは API リクエストの URL とクエリを含み、ロングタスクは使う相手が
    // いないため、v7 で既定有効になっているこの2つは切っておく。
    trackResources: false,
    trackLongTasks: false,
    // view.url と view.referrer は beforeSend で変更できるフィールド。
    beforeSend: (event) => {
      event.view.url = stripQuery(event.view.url);
      if (event.view.referrer) {
        event.view.referrer = stripQuery(event.view.referrer);
      }
      return true;
    },
  });
  // ユーザー識別子（clerkId・メールなど）は設定しない。

  syncReplayRecording();

  // RUM は pushState / replaceState の onPostCall で view を作る。init のあとに包めば
  // こちらが外側になるので、遷移先が対象外なら元の呼び出しより前に停止できる。
  for (const method of ["pushState", "replaceState"] as const) {
    const original = history[method].bind(history);
    history[method] = (data: unknown, unused: string, url?: string | URL | null) => {
      if (url != null && !replayAllowed(url, window.location.href)) {
        datadogRum.stopSessionReplayRecording();
      }
      original(data, unused, url);
      syncReplayRecording();
    };
  }
}
